import type { Order, RecipeItem } from '@library/api-user'

export type OrderDiscountPrice = {
  priceWithDiscount: string | null
  priceWithoutDiscount: string
}

const orderPhaseToHumanReadable = {
  cancelled: 'cancelled',
  awaiting_choices: 'choose',
  open: 'edit',
  cutoff: 'confirmed',
  packing: 'confirmed',
  picking: 'confirmed',
  delivery: 'dispatched',
  delivered: 'delivered',
  pre_menu: 'preMenu',
}

export function orderModel() {
  return {
    areRecipesChosenForPendingOrder: (order: Order): boolean => {
      return order.state === 'pending' && order.phase === 'open'
    },
    isOrderDelivered: (order: Order): boolean => {
      return order.phase === 'delivered'
    },
    isOrderPending: (order: Order): boolean => {
      return order.state === 'pending'
    },
    isOrderAwaitingChoices: (order: Order): boolean => {
      return order.phase === 'awaiting_choices'
    },
    isOrderCommitted: (order: Order): boolean => {
      return order.state === 'committed'
    },
    isOrderCancelled: (order: Order): boolean => {
      return order.state === 'cancelled'
    },
    isOrderEditable: (order: Order): boolean => {
      return order.phase === 'awaiting_choices' || order.phase === 'open'
    },
    isOrderOnPreMenu: (order: Order): boolean => {
      return order.phase === 'pre_menu'
    },
    isOrderRestorable: (order: Order): boolean => {
      return order.state === 'cancelled' && !!order.subscriptionId
    },
    isTransactionalOrderCancelled: (order: Order): boolean => {
      return order.state === 'cancelled' && !order.subscriptionId
    },
    isOrderFlex: (order: Order): boolean => {
      if (order.recipeItems.length === 0) return false
      const quantity = order.recipeItems[0].quantity
      return order.recipeItems.some((item) => item.quantity !== quantity)
    },
    getDeliveryDate: (order: Order) => {
      return order.deliveryDate
    },
    getOrderDeliverySlotLeadTimeId: (order: Order) => {
      return order.daySlotLeadTimeId
    },
    getPriceForOrder: (order: Order): OrderDiscountPrice => {
      return parseFloat(order.prices.totalDiscount)
        ? { priceWithDiscount: order.prices.total, priceWithoutDiscount: order.prices.grossTotal }
        : { priceWithDiscount: null, priceWithoutDiscount: order.prices.total }
    },
    getDiscountForOrder: (order: Order) => {
      const percentageOff = parseFloat(order.prices.percentageOff)

      return percentageOff ? `${percentageOff}%` : null
    },
    getRecipes: (order: Order) => {
      return order.recipeItems
    },
    getProducts: (order: Order) => {
      return order.productItems
    },
    getHumanDeliveryDate: (order: Order) => {
      return order.humanDeliveryDate
    },
    getHumanDeliveryDayTime: (order: Order) => {
      return order.deliverySlot.humanDeliveryDayTime
    },
    getShouldCutOffAt: (order: Order) => {
      return order.shouldCutoffAt
    },
    getOrderPhase: (order: Order) => {
      if (order.state === 'cancelled') {
        return 'cancelled'
      }

      return orderPhaseToHumanReadable[order.phase as keyof typeof orderPhaseToHumanReadable]
    },
    getOrderRecipeImages: (recipeItems: RecipeItem[], width: string) => {
      return recipeItems.map((recipe) => {
        const imageUrl =
          recipe.media
            .filter((m) => m.type === 'mood-image')
            .flatMap((m) => m.urls)
            .find((u) => u.width === width)?.src ?? null

        return {
          recipeId: recipe.recipeId,
          imageUrl,
        }
      })
    },
    getOrderShippingAddress: (order: Order) => {
      return {
        id: order.shippingAddress.id,
        line1: order.shippingAddress.line1,
        town: order.shippingAddress.town,
        postcode: order.shippingAddress.postcode,
      }
    },
    getOrderNumberOfPortions: (order: Order) => {
      return order.box.numPortions
    },
    getOrderNumberOfRecipes: (order: Order) => {
      return order.box.numRecipes
    },
    getOrderPeriod: (order: Order) => {
      return {
        id: order.period.id,
        start: order.period.whenStart,
        end: order.period.whenCutoff,
      }
    },
    getOrderId: (order: Order) => {
      return order.id
    },
    getOrderState: (order: Order) => {
      return order.state
    },
    getOrderType: (order: Order) => {
      return order.orderType
    },
  }
}
